import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface HeatmapCell {
  day: number;
  hour: number;
  value: number;
}

interface HeatmapPremiumProps {
  title?: string;
  subtitle?: string;
  data: HeatmapCell[];
  onExport?: () => void;
  showExport?: boolean;
}

// Paleta de cores oficial (0% → 100%) - Suave e Premium
const COLOR_PALETTE = {
  0: '#E9F1FF',   // 0%
  20: '#BDCFFF',  // 20%
  40: '#91ABFF',  // 40%
  60: '#6588F4',  // 60%
  80: '#4F7BE8',  // 80%
  100: '#2F80ED', // 100%
};

function getHeatmapColor(value: number, maxValue: number): string {
  if (maxValue === 0) return COLOR_PALETTE[0];
  
  const percentage = (value / maxValue) * 100;
  
  if (percentage === 0) return COLOR_PALETTE[0];
  if (percentage <= 20) return COLOR_PALETTE[20];
  if (percentage <= 40) return COLOR_PALETTE[40];
  if (percentage <= 60) return COLOR_PALETTE[60];
  if (percentage <= 80) return COLOR_PALETTE[80];
  return COLOR_PALETTE[100];
}

function getTextColor(backgroundColor: string): string {
  // Cores escuras usam texto branco
  const darkColors = ['#4F7BE8', '#2F80ED'];
  return darkColors.includes(backgroundColor) ? '#FFFFFF' : '#1F2937';
}

export function HeatmapPremium({ 
  title = translateInterfaceText("Mapa de Calor (Volume)"),
  subtitle = translateInterfaceText("Plays únicos por dia e horário"),
  data,
  onExport,
  showExport = true
}: HeatmapPremiumProps) {
  useLanguage();
  console.log('🔥 HeatmapPremium renderizado - Novo componente ativo!', { dataLength: data.length });
  
  const dayNames = ['SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB', 'DOM'];
  const hours = [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22];
  
  const maxValue = Math.max(...data.map(d => d.value), 1);
  
  // Encontrar horário campeão
  const topCell = data.reduce((prev, current) => 
    prev.value > current.value ? prev : current
  );

  const getDayName = (dayIndex: number) => dayNames[dayIndex];
  const getHourLabel = (hour: number) => `${hour}h`;
  
  // Função para buscar valor da célula
  const getCellValue = (day: number, hour: number): number => {
    const cell = data.find(d => d.day === day && d.hour === hour);
    return cell?.value || 0;
  };

  const handleExport = () => {
    if (onExport) {
      onExport();
    } else {
      // Mock CSV export
      let csv = 'dia,horário,valor\n';
      data.forEach(cell => {
        csv += `${getDayName(cell.day)},${cell.hour}h,${cell.value}\n`;
      });
      
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'mapa-calor.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    }
  };

  return (
    <Card className="w-full bg-white border border-[#E7EAF1] rounded-[14px] shadow-[0_4px_16px_rgba(0,0,0,0.08)] p-6 md:p-7">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="text-[15px] md:text-[17px] font-bold text-[#101828]">
            {title}
          </h3>
          <p className="text-[12px] md:text-[13px] text-[#667085] mt-1">
            {subtitle}
          </p>
        </div>
        
        {showExport && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            className="h-9 px-3 border-[#E7EAF1] rounded-[12px] hover:bg-[#F9FAFB]"
          >
            <Download className="w-4 h-4 mr-2" />
            <span className="hidden md:inline">{translateInterfaceText("Exportar CSV")}</span>
          </Button>
        )}
      </div>

      {/* Heatmap Grid */}
      <div className="overflow-x-auto -mx-2 px-2">
        <div className="min-w-[600px] md:min-w-[700px] lg:min-w-0">
          {/* Grid Container */}
          <div 
            className="inline-grid" 
            style={{ 
              gridTemplateColumns: 'auto repeat(7, minmax(28px, 36px))',
              gap: 'clamp(4px, 1vw, 6px)',
              rowGap: 'clamp(6px, 1.5vw, 8px)'
            }}
          >
            {/* Empty top-left corner */}
            <div className="h-6"></div>
            
            {/* Day names header */}
            {dayNames.map((day, index) => (
              <div key={`header-${index}`} className="h-6 flex items-center justify-center sticky top-0 bg-white z-10">
                <span className="text-[11px] font-extrabold text-[#344054]">
                  {day}
                </span>
              </div>
            ))}
            
            {/* Rows: Hours + Cells */}
            {hours.map((hour) => (
              <React.Fragment key={`row-${hour}`}>
                {/* Hour label */}
                <div className="flex items-center justify-end pr-3 w-12 h-7 md:h-9">
                  <span className="text-[11px] font-medium text-[#667085]">
                    {getHourLabel(hour)}
                  </span>
                </div>
                
                {/* Cells for each day */}
                {dayNames.map((_, dayIndex) => {
                  const value = getCellValue(dayIndex, hour);
                  const bgColor = getHeatmapColor(value, maxValue);
                  const textColor = getTextColor(bgColor);
                  const isTopCell = topCell.day === dayIndex && topCell.hour === hour;
                  
                  return (
                    <TooltipProvider key={`cell-${dayIndex}-${hour}`} delayDuration={100}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div
                            className={`
                              relative w-7 h-7 md:w-9 md:h-9 rounded-[4px] md:rounded-[6px]
                              transition-all duration-200 cursor-pointer
                              shadow-[0_2px_6px_rgba(0,0,0,0.07)]
                              hover:ring-2 hover:ring-[#2F80ED] hover:ring-opacity-60
                              hover:shadow-[0_4px_12px_rgba(47,128,237,0.25)] hover:scale-110
                              ${isTopCell ? 'ring-2 ring-[#2F80ED]' : ''}
                            `}
                            style={{ 
                              backgroundColor: bgColor,
                              opacity: value === 0 ? 0.4 : 0.85 + (value / maxValue) * 0.15
                            }}
                          >
                            <span 
                              className="absolute inset-0 flex items-center justify-center text-[10px] md:text-[11px] font-bold"
                              style={{ color: textColor }}
                            >
                              {value}
                            </span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent 
                          className="bg-[#2F80ED] text-white border-none rounded-[10px] px-4 py-3 shadow-2xl animate-in fade-in-0 zoom-in-95 duration-200"
                        >
                          <div className="text-[13px] font-semibold">
                            {getDayName(dayIndex)} • {hour}h-{hour + 2}h
                          </div>
                          <div className="text-[12px] opacity-95 mt-1.5 flex items-center gap-2">
                            <span className="font-medium">{translateInterfaceText("Plays únicos:")}</span>
                            <span className="font-bold">{value}</span>
                          </div>
                          <div className="text-[11px] opacity-90 mt-0.5">
                            {((value / maxValue) * 100).toFixed(1)}{translateInterfaceText("% do horário de pico")}</div>
                          {isTopCell && (
                            <div className="text-[11px] font-medium opacity-90 mt-2 pt-2 border-t border-white/30">
                              {translateInterfaceText("🏆 Horário Campeão")}</div>
                          )}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Legend and Tip */}
      <div className="mt-6 pt-5 border-t border-[#E7EAF1] space-y-4">
        {/* Color Scale Legend */}
        <div className="flex items-center justify-center gap-2">
          <span className="text-xs font-medium text-[#667085]">{translateInterfaceText("Intensidade:")}</span>
          <div className="flex gap-1.5">
            {Object.values(COLOR_PALETTE).map((color, index) => (
              <div 
                key={index}
                className="w-5 h-5 md:w-6 md:h-6 rounded-[4px] shadow-sm"
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
          <div className="flex items-center gap-2 text-xs text-[#667085]">
            <span>{translateInterfaceText("Menor")}</span>
            <span>→</span>
            <span>{translateInterfaceText("Maior")}</span>
          </div>
        </div>
        
        {/* Tip */}
        <div className="p-4 bg-[#F0F9FF] border border-[#BDCFFF] rounded-lg">
          <p className="text-[12px] md:text-[13px] text-[#667085] leading-relaxed text-center">
            💡 <strong>{translateInterfaceText("Dica:")}</strong> {" "}{translateInterfaceText("As cores mais intensas indicam horários com maior volume de plays.")}<br className="hidden md:block" />
            {translateInterfaceText("Use esses dados para planejar estratégias e otimizar suas conversões.")}</p>
        </div>
      </div>
    </Card>
  );
}
